/**
 * QueryBuilder Store - Helpers.
 *
 * Pure utility functions used by store actions, the script emitter, and
 * the UI validator. No React, no Zustand — just data shape manipulation.
 *
 *   - newId / blank factories      → seed fresh entities
 *   - nextXName helpers            → numbered defaults
 *   - locateStep / mapStepInTree   → recurse into LoopStep bodies
 *   - walkAllSteps                 → pre-order flatten
 *   - cloneStepsWithFreshIds       → recursive id rewrite
 *   - normalizeHydratedSteps       → migrate sidecar shapes that bypass persist()
 *   - collectStepIssues            → validation rules for the UI badge layer
 *   - updateStep / containerForTarget → state-update primitives used by the store
 */
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  BuilderTab,
  TransformPipeline,
  TransformStep,
  TransformStepType,
} from "types/queryBuilder.types"
import { QueryBuilderStore } from "./types"

export const DEFAULT_XTAB_OPERATORS: STATISTIC_TYPE[] = [
  STATISTIC_TYPE.N,
  STATISTIC_TYPE.COL_PERCENT,
]

export const DEFAULT_DTAB_OPERATORS: STATISTIC_TYPE[] = [
  STATISTIC_TYPE.N,
  STATISTIC_TYPE.AVG,
]

export const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `b_${Date.now()}_${Math.floor(Math.random() * 10000)}`

/** Sanitise a free-text builder name into something the DATA(Q) tokenizer
 *  accepts as a bare identifier — letters, digits, underscore, starting with
 *  a letter or underscore. Empty / fully-stripped names fall back to "table"
 *  so the default never collides on the leading-character rule. */
export const sanitiseOutputIdentifier = (name: string): string => {
  let cleaned = name.replace(/[^a-zA-Z0-9_]/g, "_").substring(0, 32)
  if (!/^[A-Za-z_]/.test(cleaned)) cleaned = "_" + cleaned
  return cleaned || "_table"
}

/** Default output name for a new builder. Picks `tbl_<sanitised name>` and
 *  appends a numeric suffix only if a collision is found among the existing
 *  builders. */
export const defaultBuilderOutputName = (
  name: string,
  existing: BuilderTab[]
): string => {
  const base = `tbl_${sanitiseOutputIdentifier(name)}`
  const taken = new Set(existing.map((b) => b.outputName))
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}

export const blankBuilder = (
  name: string,
  defaultPipelineId: string | null = null,
  existing: BuilderTab[] = []
): BuilderTab => ({
  id: newId(),
  name,
  outputName: defaultBuilderOutputName(name, existing),
  operation: TABLE_OPERATION_TYPE.XTAB,
  rowVariables: [],
  columnVariables: [],
  selectedOperators: DEFAULT_XTAB_OPERATORS,
  universeFilter: null,
  filterGroups: [],
  isTransposed: false,
  transformPipelineId: defaultPipelineId,
})

export const blankPipeline = (name: string): TransformPipeline => ({
  id: newId(),
  name,
  steps: [],
})

export const blankTransformStep = (
  type: TransformStepType,
  defaultName: string
): TransformStep => {
  switch (type) {
    case "combine_or":
      return {
        id: newId(),
        type,
        mode: "create",
        name: defaultName,
        sources: [],
      }
    case "and_combine":
      return {
        id: newId(),
        type,
        mode: "create",
        name: defaultName,
        sources: [],
      }
    case "negate":
      return {
        id: newId(),
        type,
        mode: "create",
        name: defaultName,
        source: "",
      }
    case "cleanup":
      return { id: newId(), type, source: "", mode: "false" }
    case "qtable":
      return { id: newId(), type, name: defaultName }
    case "field_decl":
      return {
        id: newId(),
        type,
        name: defaultName,
        fieldType: "BOOL",
        initialValue: "",
      }
    case "append":
      return { id: newId(), type, parent: "", children: [] }
    case "remove":
      return { id: newId(), type, targets: [] }
    case "conditional_assign":
      return {
        id: newId(),
        type,
        target: defaultName,
        valueMode: "true",
        valueField: "",
        conditions: [],
      }
    case "copy_structure":
      return { id: newId(), type, target: defaultName, source: "" }
    case "loop":
      return {
        id: newId(),
        type,
        variable: "ITEM",
        // "None" pre-selected because excluding the null bucket is the
        // canonical default in real preprocessing scripts. The user can
        // remove it or add others (Other, Refused, etc.).
        iteration: { kind: "field", source: "", excludedValues: ["None"] },
        body: [],
      }
    case "define":
      return { id: newId(), type, symbol: "MY_VALUE", value: "" }
    case "appendstruct":
      return { id: newId(), type, target: "", source: "", alias: "" }
    case "calc":
      return {
        id: newId(),
        type,
        mode: "create",
        target: defaultName,
        fieldType: "INT",
        expression: "",
      }
    case "cat":
      // Categories start empty — they auto-populate from the dropped
      // container's BOOL children in the editor.
      return {
        id: newId(),
        type,
        target: "",
        source: "",
        categories: [],
      }
    case "weighting":
      return {
        id: newId(),
        type,
        mode: "apply",
        weightsField: "",
        destField: "weights",
        // Start with one empty rim dimension; its targets auto-populate from
        // the dropped container's BOOL children. The user can add more.
        dimensions: [{ container: "", targets: [] }],
        filter: "",
        normalize: "none",
        normalizeValue: "",
      }
  }
}

export const stepDisplayLabel = (s: TransformStep): string => {
  switch (s.type) {
    case "cleanup":
      return s.source
    case "append":
      return s.parent
    case "remove":
      return s.targets[0] ?? ""
    case "conditional_assign":
      return s.target
    case "copy_structure":
      return s.target
    case "qtable":
    case "field_decl":
    case "combine_or":
    case "and_combine":
    case "negate":
      return s.name
    case "loop":
      return s.variable
    case "define":
      return s.symbol
    case "appendstruct":
    case "calc":
    case "cat":
      return s.target
    case "weighting":
      return s.mode === "apply" ? s.weightsField : s.destField
  }
}

export const nextBuilderName = (existing: BuilderTab[]): string =>
  nextNumberedName(existing.map((b) => b.name), "Builder")

export const nextStepName = (existing: TransformStep[]): string =>
  nextNumberedName(existing.map(stepDisplayLabel), "Step")

export const nextPipelineName = (existing: TransformPipeline[]): string =>
  nextNumberedName(existing.map((p) => p.name), "Pipeline")

export const nextNumberedName = (names: string[], prefix: string): string => {
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const re = new RegExp(`^${escaped} (\\d+)$`)
  const used = new Set(
    names
      .map((n) => re.exec(n)?.[1])
      .filter((m): m is string => !!m)
      .map((m) => parseInt(m, 10))
  )
  let n = 1
  while (used.has(n)) n++
  return `${prefix} ${n}`
}

export const dedupePush = (list: string[], variable: string): string[] =>
  list.includes(variable) ? list : [...list, variable]

export const updateActive = (
  state: QueryBuilderStore,
  transform: (b: BuilderTab) => BuilderTab
): Partial<QueryBuilderStore> => {
  if (!state.activeBuilderId) return {}
  return {
    builders: state.builders.map((b) =>
      b.id === state.activeBuilderId ? transform(b) : b
    ),
  }
}

// ============================================================
// Recursive step-tree helpers — let mutators and emitters walk into LoopStep
// bodies without each having to know about nesting.
// ============================================================

/** Locate a step by id anywhere in the recursive tree. */
export interface StepLocation {
  /** The array that contains the step (mutate in place by index). */
  container: TransformStep[]
  index: number
  /** The id of the LoopStep whose `body` contains this step, or null at root. */
  parentLoopId: string | null
}

export const locateStep = (
  steps: TransformStep[],
  stepId: string,
  parentLoopId: string | null = null
): StepLocation | null => {
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i]
    if (s.id === stepId) {
      return { container: steps, index: i, parentLoopId }
    }
    if (s.type === "loop") {
      const inner = locateStep(s.body, stepId, s.id)
      if (inner) return inner
    }
  }
  return null
}

/** Immutably rewrite a step (matched by id) anywhere in the tree. Recurses
 *  into loop bodies so per-step mutators (rename, addStepSource, etc.) keep
 *  working without each having to handle nesting. */
export const mapStepInTree = (
  steps: TransformStep[],
  stepId: string,
  transform: (s: TransformStep) => TransformStep
): TransformStep[] =>
  steps.map((s) => {
    if (s.id === stepId) return transform(s)
    if (s.type === "loop") {
      return { ...s, body: mapStepInTree(s.body, stepId, transform) }
    }
    return s
  })

/** Pre-order flatten of the recursive step tree. Used by helper-field
 *  detectors so a `conditional_assign` deep inside a loop still triggers
 *  the `Boolean//True` / `Blank` prologue emission. */
export const walkAllSteps = (steps: TransformStep[]): TransformStep[] => {
  const out: TransformStep[] = []
  const visit = (arr: TransformStep[]): void => {
    for (const s of arr) {
      out.push(s)
      if (s.type === "loop") visit(s.body)
    }
  }
  visit(steps)
  return out
}

/** Recursively rewrite ids so a duplicated pipeline doesn't alias body-step
 *  ids with the original. */
export const cloneStepsWithFreshIds = (
  steps: TransformStep[]
): TransformStep[] =>
  steps.map((s) =>
    s.type === "loop"
      ? { ...s, id: newId(), body: cloneStepsWithFreshIds(s.body) }
      : { ...s, id: newId() }
  )

/** Normalise a step tree loaded from outside the Zustand persist boundary
 *  (e.g. a sidecar from the backend). Without this, pipelines saved under
 *  an older schema crash when the UI tries to access fields that didn't
 *  exist back then. Handles the v8→v9 LoopFieldIteration migration today
 *  (excludeNone boolean → excludedValues string[]) and recurses into loop
 *  bodies. New normalisations get added here as the schema evolves. */
export const normalizeHydratedSteps = (
  steps: TransformStep[]
): TransformStep[] =>
  steps.map((raw) => {
    if (raw.type !== "loop") return raw
    const loop = raw as unknown as Record<string, unknown> & {
      iteration?: Record<string, unknown>
      body?: TransformStep[]
    }
    const it = loop.iteration
    if (it && it.kind === "field") {
      const fieldIt = it as Record<string, unknown>
      if (!Array.isArray(fieldIt.excludedValues)) {
        fieldIt.excludedValues =
          "excludeNone" in fieldIt && fieldIt.excludeNone ? ["None"] : []
      }
      delete fieldIt.excludeNone
    }
    if (Array.isArray(loop.body)) {
      loop.body = normalizeHydratedSteps(loop.body)
    }
    return loop as unknown as TransformStep
  })

// ============================================================
// Pipeline validation — pure, called from the UI at render time. Light
// enough on a small step tree that we don't bother memoising.
// ============================================================

export interface StepIssue {
  severity: "warn" | "error"
  message: string
}

export type PipelineIssues = Map<string, StepIssue[]>

/** The leaf path a step *creates* on each run, or null if it doesn't create
 *  one. Used by the loop-scope validator: a step that creates the same leaf
 *  every iteration will succeed once and fail forever after.
 *
 *  Includes `append` because its script emission prepends a `QTABLE
 *  <<parent>>` to auto-create the container — and the engine's
 *  `CreateQTable` throws if the leaf already exists. Inside a loop with a
 *  constant parent, the second iteration's auto-prepended QTABLE collides.
 *
 *  `appendstruct` is intentionally excluded — it does NOT auto-prepend, so
 *  repeated use against a constant target is safe (each call just appends
 *  more children). It has its own validator below for the "target doesn't
 *  exist yet" case. */
const stepCreatesLeaf = (s: TransformStep): string | null => {
  switch (s.type) {
    case "qtable":
      return s.name.trim() || null
    case "field_decl":
      return s.name.trim() || null
    case "conditional_assign":
      return s.target.trim() || null
    case "copy_structure":
      return s.target.trim() || null
    case "combine_or":
    case "and_combine":
      return s.mode === "create" ? s.name.trim() || null : null
    case "negate":
      return s.mode === "create" ? s.name.trim() || null : null
    case "append":
      return s.parent.trim() || null
    case "calc":
      return s.mode === "create" ? s.target.trim() || null : null
    case "cat":
      // CAT assigns into a container's existing BOOL children — it doesn't
      // create any new leaf, so re-running it (e.g. in a loop) is safe.
      return null
    case "weighting":
      // calculate mode declares the REAL weights field each run.
      return s.mode === "calculate" ? s.destField.trim() || null : null
    default:
      return null
  }
}

/** Path a step *consumes* (expects to already exist) when it runs, along
 *  with a step-specific message to surface when the path is missing. The
 *  validator checks the path against the union of (a) paths introduced by
 *  earlier steps and (b) `knownExistingPaths` (the project codebook). */
interface RequiredPath {
  path: string
  message: string
}

const stepRequiresExisting = (s: TransformStep): RequiredPath | null => {
  switch (s.type) {
    case "appendstruct": {
      const t = s.target.trim()
      return t
        ? {
            path: t,
            message:
              `APPENDSTRUCT needs "${t}" to already exist. ` +
              `Add a "Create container (QTable)" step earlier in the pipeline.`,
          }
        : null
    }
    case "combine_or": {
      // "OR into existing" folds new sources into a field that must exist.
      // "Create new field" declares the field and is therefore safe.
      if (s.mode !== "into_existing") return null
      const t = s.name.trim()
      return t
        ? {
            path: t,
            message:
              `"OR into existing" needs the target field "${t}" to already ` +
              `exist. Pick "Create new field" instead, or add a step that ` +
              `declares "${t}" earlier in the pipeline.`,
          }
        : null
    }
    case "and_combine": {
      if (s.mode !== "into_existing") return null
      const t = s.name.trim()
      return t
        ? {
            path: t,
            message:
              `"AND into existing" needs the target field "${t}" to already ` +
              `exist. Pick "Create new field" instead, or add a step that ` +
              `declares "${t}" earlier in the pipeline.`,
          }
        : null
    }
    case "negate": {
      // "Invert in place" flips an existing field; "Create new" copies
      // from `source` and then NOTs it — both need `source` to exist.
      const src = s.source.trim()
      return src
        ? {
            path: src,
            message:
              s.mode === "in_place"
                ? `"Invert in place" needs the source field "${src}" to already exist.`
                : `"Invert" copies from "${src}" — that source field must already exist.`,
          }
        : null
    }
    case "cleanup": {
      const src = s.source.trim()
      return src
        ? {
            path: src,
            message: `"Fill blank values" needs the source field "${src}" to already exist.`,
          }
        : null
    }
    case "copy_structure": {
      const src = s.source.trim()
      return src
        ? {
            path: src,
            message: `"Clone container structure" needs the source container "${src}" to already exist.`,
          }
        : null
    }
    case "calc": {
      // "CALC into existing" writes into a field that must already exist.
      // "Create new field" declares the field and is therefore safe.
      if (s.mode !== "into_existing") return null
      const t = s.target.trim()
      return t
        ? {
            path: t,
            message:
              `"CALC into existing" needs the target field "${t}" to already ` +
              `exist. Pick "Create new field" instead, or add a step that ` +
              `declares "${t}" earlier in the pipeline.`,
          }
        : null
    }
    case "cat": {
      // CAT adds BOOL children under an existing container — the container
      // itself must be created earlier (a "Create container (QTable)" step)
      // or already be present in the codebook.
      const t = s.target.trim()
      return t
        ? {
            path: t,
            message:
              `CAT needs the category container "${t}" to already exist. ` +
              `Add a "Create container (QTable)" step earlier in the pipeline, ` +
              `or make sure "${t}" is in the project data.`,
          }
        : null
    }
    case "weighting": {
      if (s.mode === "apply") {
        const w = s.weightsField.trim()
        return w
          ? {
              path: w,
              message: `"Apply weights" needs the weights field "${w}" to already exist (a REAL column of per-respondent weights).`,
            }
          : null
      }
      // calculate: each dimension's container must exist — validated
      // per-dimension in collectStepIssues (this single-path hook only
      // handles one, so it sits out the multi-variable case).
      return null
    }
    default:
      return null
  }
}

/** Validate a `field_decl` step's initial-value literal against its
 *  declared engine type. Empty values are always allowed — the engine
 *  treats an absent literal as null. Returns null for steps that aren't
 *  field_decl or that have a valid value. */
const stepInitialValueIssue = (s: TransformStep): StepIssue | null => {
  if (s.type !== "field_decl") return null
  const v = s.initialValue.trim()
  if (v === "") return null
  switch (s.fieldType) {
    case "BOOL":
      // Engine accepts both casings; accept `true` / `false` in any case
      // since we emit verbatim and the tokenizer is case-tolerant here.
      if (!/^(true|false)$/i.test(v)) {
        return {
          severity: "error",
          message: `BOOL initial value must be "true" or "false". Got "${v}".`,
        }
      }
      return null
    case "INT":
      if (!/^-?\d+$/.test(v)) {
        return {
          severity: "error",
          message: `INT initial value must be a whole number (e.g. 0, -1, 42). Got "${v}".`,
        }
      }
      return null
    case "REAL":
      if (!/^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(v)) {
        return {
          severity: "error",
          message: `REAL initial value must be a number (e.g. 1.0, -3.5, 2e10). Got "${v}".`,
        }
      }
      return null
    case "TEXT":
      return null
  }
}

/** Warn when a CAT step has category labels (auto-populated from the
 *  container's BOOL children) that don't yet have a range. The engine needs
 *  every BOOL child covered by the spec, so a blank range would either be
 *  dropped (leaving the child uncovered) or fail at run time. */
const stepCatRangeIssue = (s: TransformStep): StepIssue | null => {
  if (s.type !== "cat") return null
  const missing = s.categories.filter((c) => c.label.trim() && !c.range.trim())
  if (missing.length === 0) return null
  const names = missing
    .slice(0, 3)
    .map((c) => `"${c.label.trim()}"`)
    .join(", ")
  return {
    severity: "warn",
    message:
      `${missing.length} categor${missing.length === 1 ? "y" : "ies"} ` +
      `(${names}${missing.length > 3 ? ", …" : ""}) ${
        missing.length === 1 ? "has" : "have"
      } no range yet. ` +
      `Give every yes/no field in the container a range so they're all covered.`,
  }
}

/** Warn where the engine's CALCWEIGHTS will refuse a rim dimension's targets.
 *  Each target must be above 0 and below 100. With every category targeted,
 *  the targets must add to within 1 point of 100 (the engine scales them to
 *  100 and says so, when every respondent is in a group). With some left blank, the targets must add to less than
 *  100 so something is left for the blank ones. Whether every respondent is in
 *  a category is only known from the data, so the engine checks that. */
const stepWeightingTargetSumIssue = (s: TransformStep): StepIssue | null => {
  if (s.type !== "weighting" || s.mode !== "calculate") return null
  for (const dim of s.dimensions) {
    const populated = dim.targets.filter((t) => t.label.trim())
    const typed = populated.filter((t) => t.target.trim())
    if (typed.length === 0) continue
    const values = typed.map((t) => Number(t.target.trim()))
    if (values.some((n) => !Number.isFinite(n))) continue
    const name = dim.container.trim() || "this variable"
    const outOfRange = typed.find((t) => {
      const n = Number(t.target.trim())
      return n <= 0 || n >= 100
    })
    if (outOfRange) {
      return {
        severity: "warn",
        message: `${dim.container.trim() ? `${dim.container.trim()}//` : ""}${outOfRange.label.trim()} has a target of ${outOfRange.target.trim()}. Targets must be above 0 and below 100.`,
      }
    }
    const sum = values.reduce((a, b) => a + b, 0)
    const total = Math.round(sum * 100) / 100
    if (typed.length === populated.length) {
      if (Math.abs(sum - 100) <= 1) continue
      return {
        severity: "warn",
        message: `The ${name} targets add to ${total}. They need to add to 100.`,
      }
    }
    if (sum >= 100) {
      return {
        severity: "warn",
        message: `The ${name} targets add to ${total}, which leaves nothing for the categories without a target.`,
      }
    }
  }
  return null
}

/** Warn when a rim-weighting step asks to scale to an AVERAGE / SUM but the
 *  value is blank. The engine requires a positive number after AVERAGE/SUM, so
 *  the emitter falls back to AVERAGE 1 when it's empty — this surfaces that
 *  the chosen scaling is replaced until a value is entered. */
const stepWeightingNormalizeIssue = (s: TransformStep): StepIssue | null => {
  if (s.type !== "weighting" || s.mode !== "calculate") return null
  if (s.normalize === "none" || s.normalizeValue.trim()) return null
  return {
    severity: "warn",
    message:
      `"Scale to ${s.normalize}" needs a value (a positive number). Until you ` +
      `enter one, the weights are scaled to an average of 1.`,
  }
}

/** Names a step *introduces or touches* (in a way that should satisfy a
 *  later `appendstruct` target). A target counts as "satisfied" if any
 *  earlier step's introduced/touched name is exactly equal to it, OR is a
 *  prefix path of it (a parent container that descends to the target). */
const stepSatisfiesPath = (s: TransformStep): string | null => {
  switch (s.type) {
    case "qtable":
      return s.name.trim() || null
    case "field_decl":
      return s.name.trim() || null
    case "append":
      return s.parent.trim() || null
    case "appendstruct":
      return s.target.trim() || null
    case "copy_structure":
      return s.target.trim() || null
    case "combine_or":
    case "and_combine":
      return s.mode === "create" ? s.name.trim() || null : null
    case "negate":
      return s.mode === "create" ? s.name.trim() || null : null
    case "conditional_assign":
      return s.target.trim() || null
    case "calc":
      return s.mode === "create" ? s.target.trim() || null : null
    case "cat":
      // CAT consumes an existing container and assigns into its children; it
      // doesn't introduce a new path that a later step could depend on.
      return null
    case "weighting":
      // calculate mode introduces the REAL weights field.
      return s.mode === "calculate" ? s.destField.trim() || null : null
    default:
      return null
  }
}

/** Walk the pipeline and collect per-step issues. Three rules today:
 *  1. Body steps inside a loop whose new-leaf path doesn't reference any
 *     in-scope loop variable — the engine creates it on iteration 1, then
 *     errors on every subsequent iteration.
 *  2. Any step that *consumes* a path (APPENDSTRUCT target, BUILD NET in
 *     "into existing" mode, "Invert in place" source, Cleanup source,
 *     Copy-structure source) must reference a path satisfied by an
 *     earlier step OR present in the project codebook. The message is
 *     step-specific (`stepRequiresExisting`).
 *  3. A `field_decl` step's initial-value literal must match its declared
 *     engine type — BOOL accepts true/false, INT accepts a whole number,
 *     REAL accepts any number, TEXT accepts anything. Empty literals are
 *     allowed (the field starts null). */
export const collectStepIssues = (
  steps: TransformStep[],
  knownExistingPaths: Set<string> = new Set()
): PipelineIssues => {
  const issues: PipelineIssues = new Map()
  // Names introduced/touched by earlier steps PLUS paths that already exist
  // in the project (from the codebook). Pre-order walk extends `known` in
  // execution order; for the appendstruct check we consider any entry —
  // whether seeded from the codebook or added by an earlier step.
  const known = new Set<string>(knownExistingPaths)

  const pushIssue = (stepId: string, issue: StepIssue): void => {
    const existing = issues.get(stepId) ?? []
    existing.push(issue)
    issues.set(stepId, existing)
  }

  const visit = (arr: TransformStep[], scope: string[]): void => {
    for (const s of arr) {
      // Rule 1: loop-scope leaf collision.
      if (scope.length > 0) {
        const leaf = stepCreatesLeaf(s)
        if (leaf) {
          const lastSegment = leaf.split("//").pop() ?? leaf
          // Engine substitution is plain substring — no word boundaries —
          // so the leaf "references" a variable if that variable's name
          // appears anywhere inside the last segment.
          const referencesAny = scope.some(
            (v) => v.trim() !== "" && lastSegment.includes(v.trim())
          )
          if (!referencesAny) {
            const vars = scope.map((v) => `\`${v}\``).join(", ")
            const list =
              scope.length === 1 ? `the loop variable ${vars}` : `one of ${vars}`
            pushIssue(s.id, {
              severity: "error",
              message:
                `This step creates "${lastSegment}" every iteration. ` +
                `It will succeed once and then fail on the next pass. ` +
                `Reference ${list} somewhere in the name.`,
            })
          }
        }
      }

      // Rule 2: step consumes a path that must already exist (target /
      // source / etc, depending on step type).
      const requires = stepRequiresExisting(s)
      if (requires) {
        // The path counts as "satisfied" if any earlier step's introduced/
        // touched path is equal to it OR is an ancestor (the engine
        // creates intermediate containers on demand inside QTABLE path
        // walks).
        const satisfied = Array.from(known).some(
          (k) => k === requires.path || requires.path.startsWith(k + "//")
        )
        if (!satisfied) {
          pushIssue(s.id, {
            severity: "error",
            message: requires.message,
          })
        }
      }

      // Rule 3: field_decl initial-value type check.
      const valueIssue = stepInitialValueIssue(s)
      if (valueIssue) {
        pushIssue(s.id, valueIssue)
      }

      // Rule 4: CAT categories missing a range (incomplete coverage).
      const catIssue = stepCatRangeIssue(s)
      if (catIssue) {
        pushIssue(s.id, catIssue)
      }

      // Rule 5: rim-weighting targets the engine's CALCWEIGHTS would refuse.
      const weightIssue = stepWeightingTargetSumIssue(s)
      if (weightIssue) {
        pushIssue(s.id, weightIssue)
      }

      // Rule 6: AVERAGE / SUM rescaling selected but no value entered.
      const normalizeIssue = stepWeightingNormalizeIssue(s)
      if (normalizeIssue) {
        pushIssue(s.id, normalizeIssue)
      }

      // Rule 7: each weighting dimension's container must already exist (its
      // BOOL categories are read from the data). Checked here rather than via
      // stepRequiresExisting because a step can reference several containers.
      if (s.type === "weighting" && s.mode === "calculate") {
        for (const dim of s.dimensions) {
          const c = dim.container.trim()
          if (!c) continue
          const satisfied = Array.from(known).some(
            (k) => k === c || c.startsWith(k + "//")
          )
          if (!satisfied) {
            pushIssue(s.id, {
              severity: "error",
              message: `Weighting needs the variable "${c}" to already exist in the data so its categories can be weighted to targets.`,
            })
          }
        }
      }

      // Record what this step touches so later steps can reference it.
      const introduced = stepSatisfiesPath(s)
      if (introduced) known.add(introduced)

      if (s.type === "loop") {
        const childScope = s.variable.trim()
          ? [...scope, s.variable.trim()]
          : scope
        visit(s.body, childScope)
      }
    }
  }

  visit(steps, [])
  return issues
}

/** True when `descendantId` lives anywhere inside the subtree rooted at the
 *  step with id `ancestorId`. Used to refuse "drag a loop into its own body"
 *  moves before they happen. */
export const isAncestorOf = (
  steps: TransformStep[],
  ancestorId: string,
  descendantId: string
): boolean => {
  const loc = locateStep(steps, ancestorId)
  if (!loc) return false
  const node = loc.container[loc.index]
  if (node.type !== "loop") return false
  return locateStep(node.body, descendantId) !== null
}

/** Find a step's container by parentLoopId. Returns the array that
 *  contains the body for that loop (or pipeline root when null), or null
 *  if the loop id isn't found. */
export const containerForTarget = (
  steps: TransformStep[],
  parentLoopId: string | null
): TransformStep[] | null => {
  if (parentLoopId === null) return steps
  const loc = locateStep(steps, parentLoopId)
  if (!loc) return null
  const node = loc.container[loc.index]
  return node.type === "loop" ? node.body : null
}

/** Apply a transform to one step inside the active pipeline. No-op if
 *  there is no active pipeline. Recurses into loop bodies. */
export const updateStep = (
  state: QueryBuilderStore,
  stepId: string,
  transform: (s: TransformStep) => TransformStep
): Partial<QueryBuilderStore> => ({
  transformPipelines: state.transformPipelines.map((p) =>
    p.id === state.activeTransformPipelineId
      ? { ...p, steps: mapStepInTree(p.steps, stepId, transform) }
      : p
  ),
})

/** Apply an arbitrary transform to the active pipeline's top-level steps
 *  array. Note: this does NOT recurse into loop bodies — callers that need
 *  to mutate inside a loop should drive through `updateStep` (per-step) or
 *  the path-aware add/move/remove actions. */
export const updateActivePipelineSteps = (
  state: QueryBuilderStore,
  transform: (steps: TransformStep[]) => TransformStep[]
): Partial<QueryBuilderStore> => ({
  transformPipelines: state.transformPipelines.map((p) =>
    p.id === state.activeTransformPipelineId
      ? { ...p, steps: transform(p.steps) }
      : p
  ),
})
